import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { format } from 'date-fns';
import { useDispatch, useSelector } from 'react-redux';
import { getTimeRemaining, adjustDateForTimezone } from 'helpers/intl-fns';
import { selectActiveVenueTimezone } from 'selectors/general';
import { bem } from 'helpers/class-name';
import { Card, CardSection } from 'components/Card';
import Button from 'components/Button';
import Message from 'components/Message';
import { closeModal } from 'components/Modal';
import { ICONS } from 'constants/icons';
import Isvg from 'react-inlinesvg';
import {
  AttendanceInquiryType,
  AttendanceInquiryStatus,
} from '@classpass/protobuf-definitions/lib/web/models/partners/attendance_inquiry';
import { reportError } from 'helpers/error-logging';
import { ATTENDANCE_INQUIRY_RESPONSE_DEADLINE_HOURS } from 'constants/logic';
import {
  useAttendanceInquiriesQuery,
  useInvalidateAttendanceInquiries,
  useRespondToInquiriesBulkMutation,
} from './useAttendanceInquiry';
import AttendanceInquirySummary from './AttendanceInquirySummary';
import { formatReservationName } from './attendanceInquiryUtils';
import messages from './AttendanceInquiryModal.messages';
import './AttendanceInquiryModal.scss';

const { block, element } = bem('attendance-inquiry-modal');

const VIEW_MODES = {
  QUESTIONS: 'questions',
  SUMMARY: 'summary',
};

const AttendanceInquiryModal = ({ requestedInquiryNotFound }) => {
  const dispatch = useDispatch();
  const { data: inquiries = [] } = useAttendanceInquiriesQuery();
  const respondBulkMutation = useRespondToInquiriesBulkMutation({ invalidateOnSuccess: false });
  const invalidateInquiries = useInvalidateAttendanceInquiries();

  const venueTimezone = useSelector(selectActiveVenueTimezone);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [initialInquiries, setInitialInquiries] = useState(null);
  const [responses, setResponses] = useState(new Map());
  const [viewMode, setViewMode] = useState(VIEW_MODES.QUESTIONS);

  // Store initial inquiries when modal opens
  useEffect(() => {
    if (inquiries.length > 0 && initialInquiries === null) {
      setInitialInquiries(inquiries);
    }
  }, [inquiries, initialInquiries]);

  // Keep a ref to the latest invalidate function
  // This ensures we always call the current function (with current venueId) on unmount
  const invalidateInquiriesRef = useRef(invalidateInquiries);
  useEffect(() => {
    invalidateInquiriesRef.current = invalidateInquiries;
  }, [invalidateInquiries]);

  // Refresh inquiries when modal closes (only on actual unmount)
  useEffect(() => {
    return () => {
      // Cleanup: refresh inquiries when modal unmounts
      invalidateInquiriesRef.current();
    };
  }, [invalidateInquiries]);

  const displayInquiries = initialInquiries || [];
  const currentInquiry = displayInquiries[currentIndex];
  const totalInquiries = displayInquiries.length;

  const isUserNotAllowed =
    currentInquiry &&
    currentInquiry.inquiryType ===
      AttendanceInquiryType[AttendanceInquiryType.ATTENDANCE_INQUIRY_TYPE_USER_NOT_ALLOWED_IN];

  const formatDateTime = date => {
    if (!date) return '';
    const dateObj = new Date(date);
    const dateInVenueTimezone = venueTimezone ? adjustDateForTimezone(dateObj, venueTimezone) : dateObj;
    return format(dateInVenueTimezone, 'MMMM d, yyyy');
  };

  const formatTime = date => {
    if (!date) return '';
    const dateObj = new Date(date);
    const dateInVenueTimezone = venueTimezone ? adjustDateForTimezone(dateObj, venueTimezone) : dateObj;
    return format(dateInVenueTimezone, 'h:mm a');
  };

  const getTimeRemainingForInquiry = deadline => {
    if (!deadline) return { hours: ATTENDANCE_INQUIRY_RESPONSE_DEADLINE_HOURS, minutes: 0 };
    return getTimeRemaining(deadline) || { hours: 0, minutes: 0 };
  };

  const getStatusForResponse = (isPositiveResponse, isUserNotAllowedType) => {
    if (isUserNotAllowedType) {
      return isPositiveResponse
        ? AttendanceInquiryStatus.ATTENDANCE_INQUIRY_STATUS_RESOLVED_USER_ALLOWED_IN
        : AttendanceInquiryStatus.ATTENDANCE_INQUIRY_STATUS_RESOLVED_USER_NOT_ALLOWED_IN;
    }
    return isPositiveResponse
      ? AttendanceInquiryStatus.ATTENDANCE_INQUIRY_STATUS_RESOLVED_CLASS_HAPPENED
      : AttendanceInquiryStatus.ATTENDANCE_INQUIRY_STATUS_RESOLVED_CLASS_DID_NOT_HAPPEN;
  };

  const handleRadioChange = event => {
    if (!currentInquiry) return;
    const value = event.target.value === 'true';
    const status = getStatusForResponse(value, isUserNotAllowed);
    setResponses(prev => new Map(prev).set(currentInquiry.inquiryId, status));
  };

  const handleNext = () => {
    setCurrentIndex(prevIndex => {
      if (prevIndex < totalInquiries - 1) {
        return prevIndex + 1;
      }
      return prevIndex;
    });
  };

  const handlePrevious = () => {
    setCurrentIndex(prevIndex => {
      if (prevIndex > 0) {
        return prevIndex - 1;
      }
      return prevIndex;
    });
  };

  const handleSubmitAll = async () => {
    if (responses.size !== totalInquiries) {
      setError('Please respond to all inquiries before submitting.');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const responsesArray = Array.from(responses.entries()).map(([id, statusValue]) => ({
        inquiryId: id,
        status: statusValue,
      }));

      const result = await respondBulkMutation.mutateAsync(responsesArray);

      if (result.inquiryErrors && result.inquiryErrors.length > 0) {
        setError('Some responses could not be submitted. Please try again.');
        reportError(new Error('Partial failure in bulk submission'));
      } else {
        // Success - switch to summary view
        setViewMode(VIEW_MODES.SUMMARY);
      }
    } catch (err) {
      setError('Failed to submit responses. Please try again.');
      reportError(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    dispatch(closeModal());
  };

  const timeRemaining = getTimeRemainingForInquiry(currentInquiry?.responseDeadline);
  const currentResponse = currentInquiry ? responses.get(currentInquiry.inquiryId) : null;
  const isCurrentAnswered = currentResponse !== undefined;
  const allAnswered = responses.size === totalInquiries;
  const isLastInquiry = currentIndex === totalInquiries - 1;

  // Show summary view
  if (viewMode === VIEW_MODES.SUMMARY) {
    return <AttendanceInquirySummary inquiries={displayInquiries} responses={responses} onClose={handleClose} />;
  }

  if (!currentInquiry) {
    return null;
  }

  // Determine radio button values based on response
  const positiveStatus = getStatusForResponse(true, isUserNotAllowed);
  const negativeStatus = getStatusForResponse(false, isUserNotAllowed);
  let selectedValue = '';
  if (currentResponse === positiveStatus) {
    selectedValue = 'true';
  } else if (currentResponse === negativeStatus) {
    selectedValue = 'false';
  }

  return (
    <Card className={block()} flush>
      <CardSection className={element('header')}>
        <div className={element('progress-container')}>
          <div className={element('progress-dots')} key={`dots-${currentIndex}`}>
            {Array.from({ length: totalInquiries }, (_, index) => {
              const isActive = index === currentIndex;
              return (
                <div
                  key={index}
                  className={element('progress-dot', {
                    active: isActive,
                  })}
                />
              );
            })}
          </div>
          <div className={element('close-button')}>
            <button
              type="button"
              onClick={handleClose}
              name="close-button"
              aria-label="close-button"
              className={element('close-icon')}>
              <Isvg src={ICONS.cross} />
            </button>
          </div>
        </div>
        <div className={element('progress-text')} key={`progress-${currentIndex}`}>
          <Message message={messages.progress} values={{ current: currentIndex + 1, total: totalInquiries }} />
        </div>
        <div className={element('deadline')} key={`deadline-${currentInquiry.inquiryId}`}>
          <Message
            message={messages.responseDeadline}
            values={{ hours: timeRemaining.hours, minutes: timeRemaining.minutes }}
          />
        </div>
      </CardSection>

      <CardSection className={element('content')}>
        {requestedInquiryNotFound && currentIndex === 0 && (
          <div className={element('error')} role="alert">
            <Message message={messages.requestedInquiryNotFound} />
          </div>
        )}
        <div className={element('description')}>
          {isUserNotAllowed ? (
            <>
              <div>
                <Message message={messages.greetingHello} values={{ name: currentInquiry.userName }} />
              </div>
              <div>
                <Message message={messages.descriptionUserNotAllowed} />
              </div>
            </>
          ) : (
            <Message
              message={messages.description}
              values={{ hours: timeRemaining.hours, minutes: timeRemaining.minutes }}
            />
          )}
        </div>

        <div className={element('reservation-details')}>
          <div className={element('details-title')}>
            <Message message={messages.reservationDetailsTitle} />
          </div>
          <div className={element('details-content')}>
            <div className={element('detail-row')}>
              <span className={element('detail-label')}>
                <Message message={messages.userName} />
              </span>
              <span className={element('detail-value')}>{currentInquiry.userName}</span>
            </div>
            <div className={element('detail-row')}>
              <span className={element('detail-label')}>
                <Message message={messages.reservationName} />
              </span>
              <span className={element('detail-value')}>{formatReservationName(currentInquiry.reservationName)}</span>
            </div>
            <div className={element('detail-row')}>
              <span className={element('detail-label')}>
                <Message message={messages.date} />
              </span>
              <span className={element('detail-value')}>{formatDateTime(currentInquiry.reservationDatetime)}</span>
            </div>
            <div className={element('detail-row')}>
              <span className={element('detail-label')}>
                <Message message={messages.time} />
              </span>
              <span className={element('detail-value')}>{formatTime(currentInquiry.reservationDatetime)}</span>
            </div>
          </div>
        </div>

        <div className={element('radio-group-container')}>
          <div className={element('radio-group-label')}>
            <Message message={messages.selectResponse} />
          </div>
          <div className={element('radio-group')}>
            <label className={element('radio-option')}>
              <input
                type="radio"
                name={`response-${currentInquiry.inquiryId}`}
                value="true"
                checked={selectedValue === 'true'}
                onChange={handleRadioChange}
                className={element('radio-input')}
              />
              <span className={element('radio-label')}>
                <Message message={isUserNotAllowed ? messages.buttonUserAllowedIn : messages.buttonTookPlace} />
              </span>
            </label>
            <label className={element('radio-option')}>
              <input
                type="radio"
                name={`response-${currentInquiry.inquiryId}`}
                value="false"
                checked={selectedValue === 'false'}
                onChange={handleRadioChange}
                className={element('radio-input')}
              />
              <span className={element('radio-label')}>
                <Message message={isUserNotAllowed ? messages.buttonUserNotAllowedIn : messages.buttonDidNotOccur} />
              </span>
            </label>
          </div>
        </div>
      </CardSection>

      <CardSection className={element('actions')}>
        {error && (
          <div className={element('error')} role="alert">
            {error}
          </div>
        )}
        <div className={element('navigation-buttons')}>
          <Button styled="primary-outline" onClick={handlePrevious} disabled={currentIndex === 0 || isSubmitting}>
            <Message message={messages.buttonBack} />
          </Button>
          {isLastInquiry ? (
            <Button
              styled="primary"
              onClick={handleSubmitAll}
              disabled={!allAnswered || isSubmitting}
              waiting={isSubmitting}>
              <Message message={messages.buttonSubmitAll} />
            </Button>
          ) : (
            <Button styled="primary" onClick={handleNext} disabled={!isCurrentAnswered || isSubmitting}>
              <Message message={messages.buttonNext} />
            </Button>
          )}
        </div>
      </CardSection>
    </Card>
  );
};

AttendanceInquiryModal.propTypes = {
  requestedInquiryNotFound: PropTypes.bool,
};

export default AttendanceInquiryModal;
